import { useEffect, useMemo, useRef, useState } from 'react';

import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ChevronDown from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-down';
import ChevronUp from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-up';
import ArrowRight from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-right';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';
import { GLOBAL } from 'saddlebag-browser';

import { logMiniEvent } from '../../../logMiniEvent';
import { useMBDContext } from '../../common/MBD/useMBDContext';
import DealCardV2 from '../Deal';
import getDealsCommonTrackingProps from '../getDealsCommonTrackingProps';

import NoDeals from './NoDeals';

import type { DealCardData } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';
import type { DealsListProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsList';

import STYLES from './DealsList.module.scss';

const ChevronDownArrow = withRtlSupport(ChevronDown);
const ChevronUpArrow = withRtlSupport(ChevronUp);
const LongArrowRight = withRtlSupport(ArrowRight);

const DealsList = (props: DealsListProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const $window = GLOBAL.getWindow();
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = rawComponentName;
  const [isDealsExpanded, setIsDealsExpanded] = useState<boolean>(true);

  const {
    countryViewUrl,
    deals,
    dealsLimit,
    destinationCode,
    isMobile = false,
    originCode,
    pageTypeOptions,
    priceBandStrings,
    priceBandsDisplayState,
    shouldShow3by2Formation,
    strings,
    tabName,
    trackingContext,
  } = props;

  const collapsedStateDealsLimit = () => {
    const half = dealsLimit / 2;
    if (isMobile) return half;
    return half % 2 ? half + 1 : half;
  };

  const collapsedLimit = collapsedStateDealsLimit();

  const visibleDeals = useMemo(() => {
    const visibleDealsLimit = isDealsExpanded ? dealsLimit : collapsedLimit;
    const visibleDealsAmount =
      deals.length > visibleDealsLimit ? visibleDealsLimit : deals.length;
    return deals.slice(0, visibleDealsAmount);
  }, [deals, dealsLimit, isDealsExpanded, collapsedLimit]);

  useEffect(() => {
    if (visibleDeals.length > 0) {
      logMiniEvent('DealsEvolvedEvent', 'deals_evolved_component', {
        deals_common: getDealsCommonTrackingProps(props),
        event_type: 'DATA_LOADED',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToWithOffset = (el: HTMLElement, offset = 72) => {
    const y = el.getBoundingClientRect().top + $window.scrollY - offset;
    $window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const onToggleShow = () => {
    if (isDealsExpanded && rootRef?.current) {
      scrollToWithOffset(rootRef.current, 72);

      rootRef.current.focus();
    }

    logMiniEvent('DealsEvolvedEvent', 'deals_evolved_component', {
      deals_common: getDealsCommonTrackingProps(props),
      event_type: isDealsExpanded
        ? 'SEE_LESS_DEALS_CLICKED'
        : 'SEE_MORE_DEALS_CLICKED',
    });

    setIsDealsExpanded((prev) => !prev);
  };

  if (deals.length === 0 && strings.noDealsTips) {
    return (
      <div className={STYLES.DealsList}>
        <NoDeals
          noDealsCtaText={strings.noDealsCta}
          noDealsTipsText={strings.noDealsTips}
          originCode={originCode}
          destinationCode={destinationCode ?? undefined}
        />
      </div>
    );
  }

  const showButton = deals.length > collapsedLimit;
  const numberOfDealsReturned = deals.length;

  return (
    <div ref={rootRef} className={STYLES.DealsList}>
      {visibleDeals.length > 0 && (
        <>
          <div
            className={STYLES.DealsList__grid}
            data-testid="deal-grid-layout"
          >
            {visibleDeals.map((deal: DealCardData, index: number) => {
              const partnerIndex = index % 2 === 0 ? index + 1 : index - 1;
              const partner = visibleDeals[partnerIndex];
              // In the 2-column desktop layout, reserve badge space only when
              // the adjacent card in the same row has a badge and this one doesn't,
              // so both cards remain the same height without wasting space on
              // rows where neither card has a badge.
              const showBadgePlaceholder =
                !!priceBandStrings &&
                !shouldShow3by2Formation &&
                !isMobile &&
                !deal.priceBandIndicator &&
                !!partner?.priceBandIndicator;

              return (
                <div
                  className={classNames({
                    [STYLES.DealsList__dealPanelFor3Cards]:
                      shouldShow3by2Formation,
                    [STYLES.DealsList__dealPanel]: !shouldShow3by2Formation,
                  })}
                  key={deal.id}
                >
                  <DealCardV2
                    {...deal}
                    strings={strings}
                    trackingContext={trackingContext}
                    pageTypeOptions={pageTypeOptions}
                    priceBandStrings={priceBandStrings}
                    priceBandsDisplayState={priceBandsDisplayState}
                    showBadgePlaceholder={showBadgePlaceholder}
                    cardIndex={index + 1}
                    numberOfDealsRequested={dealsLimit}
                    numberOfDealsReturned={numberOfDealsReturned}
                    tabName={tabName}
                  />
                </div>
              );
            })}
          </div>

          {countryViewUrl && (
            <div className={STYLES.DealsList__showNavigationButton}>
              <BpkButton
                href={countryViewUrl}
                data-testid="country-view"
                data-tracking-element-id={`${mbdComponentName}_button_expand`}
                data-tracking-common-params={buildMBDParam(
                  `${mbdComponentName}_button_expand`,
                  {
                    number_of_deals_requested: dealsLimit.toString(),
                    number_of_deals_returned: numberOfDealsReturned.toString(),
                    price_bands_display_state: priceBandsDisplayState,
                  },
                )}
                trailingIcon={<LongArrowRight />}
              >
                {strings.dealsEvolved_dealsList_show_more_deals}
              </BpkButton>
            </div>
          )}

          {showButton && !countryViewUrl && (
            <div className={STYLES.DealsList__showMore}>
              <BpkButton
                onClick={onToggleShow}
                data-testid="expand-collapse"
                type={BUTTON_TYPES.link}
                data-tracking-element-id={`${mbdComponentName}_button_${
                  isDealsExpanded ? 'collapse' : 'expand'
                }`}
                data-tracking-common-params={buildMBDParam(
                  `${mbdComponentName}_button_${
                    isDealsExpanded ? 'collapse' : 'expand'
                  }`,
                  {
                    number_of_deals_requested: dealsLimit.toString(),
                    number_of_deals_returned: numberOfDealsReturned.toString(),
                    price_bands_display_state: priceBandsDisplayState,
                  },
                )}
                implicit
              >
                <BpkText textStyle={TEXT_STYLES.label1}>
                  {isDealsExpanded
                    ? strings.dealsEvolved_dealsList_show_fewer_deals
                    : strings.dealsEvolved_dealsList_show_more_deals}
                </BpkText>
                {isDealsExpanded ? <ChevronUpArrow /> : <ChevronDownArrow />}
              </BpkButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DealsList;
